/** A dedicated-resource owner in whatever detail the payload provides. */
export interface DedicatedUserLike {
  id: string;
  name: string;
  profile_photo_url?: string;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/**
 * Normalizes a dedicated-users payload (GET /resources/:id/dedicated_users
 * or the `dedicated_users` field on search results) into uniform rows.
 * Accepts a bare array or a container with `elements` / `dedicated_users` /
 * `users`, with entries that are user objects, `{ user: {...} }` wrappers,
 * or `{ user_id }` records.
 */
export const normalizeDedicatedUsers = (
  payload: unknown
): DedicatedUserLike[] => {
  const record = asRecord(payload);
  const rawList: unknown[] = Array.isArray(payload)
    ? payload
    : (([record?.elements, record?.dedicated_users, record?.users].find(
        Array.isArray
      ) as unknown[] | undefined) ?? []);

  const users: DedicatedUserLike[] = [];
  for (const entry of rawList) {
    const entryRecord = asRecord(entry);
    const userRecord = asRecord(entryRecord?.user) ?? entryRecord;
    const id =
      typeof userRecord?.id === "string" && userRecord.id.startsWith("user_")
        ? userRecord.id
        : typeof entryRecord?.user_id === "string"
          ? entryRecord.user_id
          : null;
    if (!id) continue;
    users.push({
      id,
      name: typeof userRecord?.name === "string" ? userRecord.name : "",
      profile_photo_url:
        typeof userRecord?.profile_photo_url === "string"
          ? userRecord.profile_photo_url
          : undefined
    });
  }
  return users;
};
